import React from 'react';

import { EmptyBar } from 'screens/analytics/commonComponents';
import { StyledGrid } from 'styles/common';

import theme from 'constants/theme';

import { Typography13 } from 'uiToolkit/Typography';

import {
  CompetencyNameCell,
  CompetencyPeriodSelectCell,
  CompetencyProgressCell,
  LevelUpRiskEstimationCell,
} from '../components/common';
import { PROGRESS_KEYS } from '../components/managerTable/tableCellComponents/ProgressBar';
import { useLevelUpPageContext } from '../context';

export const LEVELUP_FILTERS_ARRAY = [
  {
    id: 'ALL',
    display: 'All',
    valueColor: theme.palette.purple.darkv1,
    displayColor: 'textSecondary',
    borderColor: `${theme.palette.purple.darkv1}70`,
    description: 'Competencies that you have just started working on',
  },
  {
    id: PROGRESS_KEYS.NOT_CRITICAL,
    display: 'Not Critical',
    valueColor: theme.palette.purple.darkv1,
    displayColor: 'textSecondary',
    borderColor: `${theme.palette.purple.darkv1}70`,
    description: 'Competencies that you have just started working on',
  },
  {
    id: PROGRESS_KEYS.STARTING,
    display: 'Starting',
    valueColor: theme.palette.purple.darkv1,
    displayColor: 'textSecondary',
    borderColor: `${theme.palette.purple.darkv1}70`,
    description: 'Competencies that you have just started working on',
  },
  {
    id: PROGRESS_KEYS.BUILDING,
    display: 'Building',
    valueColor: theme.palette.lightOrange.main,
    displayColor: 'textSecondary',
    borderColor: `${theme.palette.lightOrange.main}70`,
    description: 'Competencies that you are showing healthy progress in',
  },
  {
    id: PROGRESS_KEYS.SKILLED,
    display: 'Skilled',
    valueColor: theme.palette.icons.green,
    displayColor: 'textSecondary',
    borderColor: `${theme.palette.icons.green}70`,
    description: 'Competencies that you are displaying consistently',
  },
  {
    id: PROGRESS_KEYS.EXCELLED,
    display: 'Excelled',
    valueColor: theme.palette.icons.blue,
    displayColor: 'textSecondary',
    borderColor: `${theme.palette.icons.blue}70`,
    description: 'Competencies where you are the go-to-person',
  },
];

export const LEVELUP_COMPETENCIES_TABLE_CELL_FIXED_WIDTH = '30rem';

export const LEVELUP_COMPETENCIES_TABLE_MAP = {
  NAME: ({
    name = '',
    subCompetencies = [],
    setIsExpanded = () => {},
    isExpanded,
    isSubManagerRow = false,
    isLast = false,
    canExpand,
    onExpandClick,
    ...props
  }) => {
    // console.log({ props, getTotalSize: props?.row?.table?.getTotalSize() });
    const dataSize = props?.row?.table?.options?.data?.length ?? 0;
    const isLastSubManagerRow = dataSize - 1 === props?.index;
    return (
      <CompetencyNameCell
        name={name}
        subCompetencies={subCompetencies}
        setIsExpanded={onExpandClick}
        isExpanded={isExpanded}
        isSubManagerRow={isSubManagerRow}
        isLast={isLastSubManagerRow}
      />
    );
  },
  PROGRESS: ({ progressBar, progress, levelledUpText }) => {
    const { percentage } = progressBar ?? {};
    return (
      <CompetencyProgressCell
        progressBar={progressBar}
        progress={progress}
        levelledUpText={levelledUpText}
        percentage={percentage}
        showPercentage
      />
    );
  },
  DUE: ({ competencyId, targetPeriodStart }) => (
    <CompetencyPeriodSelectCell competencyId={competencyId} targetPeriodStart={targetPeriodStart} />
  ),
  RISK: ({ riskEstimation }) => {
    const {
      state: { riskEstimationList = [] },
    } = useLevelUpPageContext();

    const { risk, displayName } = riskEstimationList?.find(({ risk }) => risk === riskEstimation) ?? {};

    if (!risk) {
      return (
        <StyledGrid container justify="center" alignItems="center">
          <EmptyBar />
        </StyledGrid>
      );
    }

    return (
      // <div style={{ height: '4.375rem', width: '100%' }}>
      // </div>
      <StyledGrid container>
        <LevelUpRiskEstimationCell risk={risk} displayName={displayName} />
      </StyledGrid>
    );
  },
  WEIGHTAGE: ({ competencyWeightage }) => (
    <Typography13>{competencyWeightage ? `${competencyWeightage.toFixed(2)}%` : 'NA'}</Typography13>
  ),
};

export const LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP = {
  NAME: {
    id: 'NAME',
    header: 'Competency',
    align: 'left',
    width: '30rem',
    cell: (props) =>
      LEVELUP_COMPETENCIES_TABLE_MAP.NAME({
        ...props.getValue(),
        isExpanded: props.row.getIsExpanded(),
        canExpand: props.row.getCanExpand(),
        onExpandClick: props.row.getToggleExpandedHandler(),
      }),
    accessorFn: (row) => row,
  },
  PROGRESS: {
    id: 'PROGRESS',
    header: 'Progress',
    // title: () => {
    //   return (
    //     <>
    //       Progress
    //       <LevelUpProgressTooltip />
    //     </>
    //   );
    // },
    align: 'left',
    width: '20rem',
    fixedWidth: '20rem',
    cell: (props) => LEVELUP_COMPETENCIES_TABLE_MAP.PROGRESS({ ...props.getValue() }),
    accessorFn: (row) => row,
  },
  DUE: {
    id: 'DUE',
    header: 'Target',
    isSortable: false,
    align: 'left',
    width: '8rem',
    fixedWidth: '8rem',
    cell: (props) => LEVELUP_COMPETENCIES_TABLE_MAP.DUE({ ...props.getValue() }),
    accessorFn: (row) => row,
  },
  RISK: {
    id: 'RISK',
    header: 'Risk Estimation',
    isSortable: false,
    align: 'left',
    width: 'auto',
    fixedWidth: '20rem',
    cell: (props) => LEVELUP_COMPETENCIES_TABLE_MAP.RISK({ ...props.getValue() }),
    accessorFn: (row) => row,
  },
  WEIGHTAGE: {
    id: 'WEIGHTAGE',
    header: 'Weightage',
    // title: () => {
    //   return (
    //     <>
    //       Progress
    //       <LevelUpProgressTooltip />
    //     </>
    //   );
    // },
    align: 'left',
    width: '8rem',
    fixedWidth: '8rem',
    cell: (props) => LEVELUP_COMPETENCIES_TABLE_MAP.WEIGHTAGE({ ...props.getValue() }),
    accessorFn: (row) => row,
  },
};

export const LEVELUP_COMPETENCIES_TABLE_HEADERS_MAP_SUB_MANAGER = {
  NAME: {
    id: 'NAME',
    header: 'Competency',
    align: 'left',
    width: 'auto',
    cell: (props) =>
      LEVELUP_COMPETENCIES_TABLE_MAP.NAME({
        ...props.getValue(),
        isExpanded: props.row.getIsExpanded(),
        canExpand: props.row.getCanExpand(),
        onExpandClick: props.row.getToggleExpandedHandler(),
        row: props,
        isSubManagerRow: true,
      }),
    accessorFn: (row) => row,
  },
  PROGRESS: {
    id: 'PROGRESS',
    header: 'Progress',
    // title: () => {
    //   return (
    //     <>
    //       Progress
    //       <LevelUpProgressTooltip />
    //     </>
    //   );
    // },
    align: 'center',
    width: '20rem',
    fixedWidth: '20rem',
    cell: (props) => LEVELUP_COMPETENCIES_TABLE_MAP.PROGRESS({ ...props.getValue() }),
    accessorFn: (row) => row,
  },
  DUE: {
    id: 'DUE',
    header: 'Target',
    isSortable: false,
    align: 'center',
    width: 'auto',
    fixedWidth: '10rem',
    cell: (props) => LEVELUP_COMPETENCIES_TABLE_MAP.DUE({ ...props.getValue() }),
    accessorFn: (row) => row,
  },
  RISK: {
    id: 'RISK',
    header: 'Risk Estimation',
    isSortable: false,
    align: 'center',
    width: '15rem',
    fixedWidth: '20rem',
    cell: (props) => LEVELUP_COMPETENCIES_TABLE_MAP.RISK({ ...props.getValue() }),
    accessorFn: (row) => row,
  },
};

export const getColorFromColorEnumForLevelUp = (color) => {
  switch (color) {
    case 'GREY':
      return theme.palette.grey.dark;
    case 'ORANGE':
      return theme.palette.text.orangeRed;
    case 'BLUE':
      return theme.palette.primary.main;
    case 'GREEN':
    case 'DARK_GREEN':
      return theme.palette.green.main;
    case 'PURPLE':
      return theme.palette.purple.darkv1;

    default:
      return theme.palette.grey.dark;
  }
};
